import { gql } from '@apollo/client';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, chakra, Table, Tbody, Td, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { ColumnInstance, TableInstance } from 'react-table';
import { InfoTooltip } from '~components/ui/icons/InfoTooltip';
import { ZteRowFragment } from './__generated__/ZteTable.graphql';

export type RowData = {
  id: string;
  name: string;
  handle: string;
  onboardedAdmin: string;
  lastLogin: Date | null;
};
interface Props {
  table: TableInstance<ZteRowFragment>;
}
export function ZteTable({ table }: Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = table;

  function renderHeaderWithTooltip(column: ColumnInstance<ZteRowFragment>) {
    const header = column.render('Header');

    if (header === 'Validated') {
      return (
        <>
          {header}
          <Tooltip
            label="Validated device = All CSV rows uploaded - Failed to validate."
            backgroundColor="#18365E"
            placement="top-start"
            p={4}
          >
            <chakra.span color="gray.500" cursor="pointer">
              <InfoTooltip width="4" height="4" />
            </chakra.span>
          </Tooltip>
        </>
      );
    }
    if (header === 'Devices Paired') {
      return (
        <>
          {header}
          <Tooltip
            label="Devices Paired = Validated Devices - Unpaired Devices"
            backgroundColor="#18365E"
            placement="top-start"
            p={4}
          >
            <chakra.span color="gray.500" cursor="pointer">
              <InfoTooltip width="4" height="4" />
            </chakra.span>
          </Tooltip>
        </>
      );
    }
    if (header === 'Devices claimed') {
      return (
        <>
          {header}
          <Tooltip
            label="Devices Claimed = Paired Devices which are claimed."
            backgroundColor="#18365E"
            placement="top-start"
            p={4}
          >
            <chakra.span color="gray.500" cursor="pointer">
              <InfoTooltip width="4" height="4" />
            </chakra.span>
          </Tooltip>
        </>
      );
    }
    return header;
  }

  return (
    <Table {...getTableProps()} variant="simple">
      <Thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps();
          return (
            <Tr {...headerGroupProps} key={headerGroupProps.key}>
              {headerGroup.headers.map((column) => {
                const headerProps = column.getHeaderProps(column.getSortByToggleProps());
                return (
                  <Th
                    {...headerProps}
                    key={headerProps.key}
                    color={column.isSorted ? 'gray.800' : '#486581'}
                    whiteSpace="nowrap"
                    maxW="200px"
                    pr={0}
                    _hover={
                      column.disableSortBy
                        ? {}
                        : {
                            textColor: 'gray.800',
                          }
                    }
                    onClick={(e) => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (headerProps as any).onClick?.(e);
                    }}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Box flex="1" display="flex" alignItems="center" gap="1">
                        {renderHeaderWithTooltip(column)}
                      </Box>
                      {column.canSort && (
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ChevronDownIcon display="inherit" />
                            ) : (
                              <ChevronUpIcon display="inherit" />
                            )
                          ) : (
                            <ChevronDownIcon visibility="hidden" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          );
        })}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();
          return (
            <Tr {...rowProps} key={rowProps.key}>
              {row.cells.map((cell) => {
                const cellProps = cell.getCellProps();
                return (
                  <Td {...cellProps} key={cellProps.key}>
                    {cell.render('Cell') as ReactNode}
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

ZteTable.graphql = {
  fragment: gql`
    fragment ZteRow on ZteBatch {
      id
      downloadUrl
      batchName
      claimedDisplay
      createdAt
      customerName
      fileName
      organizationName
      pairedDisplay
      status
      totalDisplay
      unClaimedDisplay
      validated
      createdBy
    }
  `,
};
